/*
 * Alloy close-match suggestions (2026-10-25). Owner: "the 'you might also like' suggestions should be suggestions that are
 * close to the one they are looking at".
 * window.AlloyRelated.pick(plan, opts) reads the plan's collection pools (printed by snippets/alloy-related-plan.liquid),
 * most specific first, through a pool section (sections/alloy-related-pool, -story or -fitted), stops as soon as it has
 * enough, and ranks the candidates: same pool level, shared style tags, Plain/Diamond style, engagement style family, center
 * shape, gender, title words, similar price. Never this piece, a shape sibling of it (same design family) or the other style
 * of a pair; never in-stock pieces next to made-to-order ones; one card per design.
 * Used by assets/related-products.js ("You may also like") and sections/story-similar.liquid (Story ring page).
 * Add ?rp_debug=1 to a product URL to log the ranking in the console.
 */
(function () {
  'use strict';
  if (window.AlloyRelated) return;

  const MAX_POOLS = 4;
  // tags that say nothing about how a piece looks
  const NOISE = new Set(['instock', 'ready-made-available', 'ready', 'live', 'essential', 'essentials', 'exclude-from-rings',
    'not-defined', 'straight', 'ring', 'rings', 'wedding-band', 'engagement-ring', 'alloy-chain', 'in-studio', 'mto',
    'december-pause', 'final-sale', 'ready-to-ship', 'alloy-instock-piece', 'fitted-band', 'mens', 'womens', 'band']);
  const STOP_WORDS = new Set(['the', 'and', 'with', 'for', 'ring', 'band', 'earrings', 'necklace', 'bracelet', 'chain',
    '14k', '14kw', '14ky', '18k', 'gold', 'white', 'yellow', 'rose', 'silver', 'ctw', 'carat']);
  const DEBUG = /[?&]rp_debug=1/.test(window.location.search);

  const splitBars = (s) => (s || '').split('|').filter(Boolean);
  const titleWords = (s) => (s || '').toLowerCase().split(/[^a-z0-9]+/).filter((w) => w.length > 2 && !STOP_WORDS.has(w) && !/^\d/.test(w));
  // engagement style family, most specific first (same order as the plan snippet)
  const styleFamily = (tags) => ['hidden-halo', 'halo', 'three-stone', 'side-stones', 'solitaire'].find((f) => tags.includes(f)) || '';
  // plain product links, like Shopify's own suggestions (pools are read on collection URLs)
  const plainLinks = (html) => html.replace(/\/collections\/[a-z0-9-]+\/products\//g, '/products/');

  async function fetchPool(entry, section) {
    let url = entry, fitted = false;
    if (url.indexOf('fitted:') === 0) { url = url.slice(7); section = 'alloy-related-pool-fitted'; fitted = true; }
    const res = await fetch(url + (url.includes('?') ? '&' : '?') + 'section_id=' + section, { credentials: 'same-origin' });
    if (!res.ok) return [];
    const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
    return Array.from(doc.querySelectorAll('[data-rp-item]')).map((node) => ({
      id: String(node.dataset.id),
      handle: node.dataset.handle,
      fam: node.dataset.fam || '',
      price: parseInt(node.dataset.price, 10) || 0,
      instock: node.dataset.st === '1',
      g: node.dataset.g || '',
      style: splitBars(node.dataset.style),
      shapes: splitBars(node.dataset.shapes),
      tags: splitBars(node.dataset.tags),
      title: node.dataset.t || '',
      fitted,
      html: plainLinks(node.innerHTML),
    }));
  }

  function isEligible(item, plan, skipIds) {
    if (skipIds.has(item.id)) return false;
    if (plan.fam && item.fam === plan.fam) return false;              // shape siblings of this design
    if (plan.pair && item.handle === plan.pair) return false;         // the other style of a pair (e.g. eternity)
    if (item.title.trim().toLowerCase() === String(plan.title || '').trim().toLowerCase()) return false;
    return plan.instock ? item.instock : !item.instock;               // in-stock stays apart from made-to-order
  }

  const hasStyle = (item, w) => item.style.includes(w) || item.tags.includes(w);
  const primMatch = (item, plan) => !!plan.prim && hasStyle(item, plan.prim);
  const primClash = (item, plan) => !!plan.prim && !primMatch(item, plan) && hasStyle(item, plan.prim === 'diamond' ? 'plain' : 'diamond');

  function score(item, plan, poolCount) {
    const myTags = new Set(splitBars(plan.tags).filter((t) => !NOISE.has(t)));
    let s = (poolCount - item.pool) * 3 + item.tags.filter((t) => myTags.has(t)).length * 2;
    // bands: Plain vs Diamond is the first thing that makes two bands alike; the other kind only fills empty places
    if (primMatch(item, plan)) s += 3;
    else if (primClash(item, plan)) s -= 4;
    if (plan.efam && (item.fitted || styleFamily(item.tags) === plan.efam)) s += 6;
    if (plan.shape && item.shapes.includes(plan.shape)) s += 1;
    if (plan.g && item.g === plan.g) s += 1;
    const mine = new Set(titleWords(plan.title));
    s += Math.min(2, titleWords(item.title).filter((w) => mine.has(w)).length);
    if (plan.price > 0 && item.price > 0) s -= Math.min(4, Math.abs(Math.log(item.price / plan.price)) * 3);
    return s;
  }

  // -> Promise<Array<{handle, html, ...}>> (best first, at most opts.want)
  async function pick(plan, opts) {
    opts = opts || {};
    const want = Math.max(1, opts.want || 4);
    const section = opts.section || 'alloy-related-pool';
    const skipIds = new Set([String(plan.id)].concat((opts.skipIds || []).map(String)));
    let pools = (plan.pools || []).filter(Boolean).slice(0, MAX_POOLS);
    // engagement ring opened in a chosen center shape (?shape=emerald): match that shape instead of the ring's first one
    const urlShape = new URLSearchParams(window.location.search).get('shape');
    if (plan.kind === 'engagement' && urlShape && /^[a-z-]+$/i.test(urlShape)) {
      const label = urlShape.toLowerCase().split('-').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
      plan = Object.assign({}, plan, { shape: label.toLowerCase() });
      pools = pools.map((p) => p.replace(/center_shapes=[^&]*/, 'center_shapes=' + encodeURIComponent(label)));
    }
    const seen = new Map();
    let eligible = [];
    for (let i = 0; i < pools.length; i++) {
      const items = await fetchPool(pools[i], section);
      items.forEach((item) => { if (!seen.has(item.id)) { item.pool = i; seen.set(item.id, item); } });
      eligible = Array.from(seen.values()).filter((item) => isEligible(item, plan, skipIds));
      if (eligible.filter((item) => !primClash(item, plan)).length >= want * 2) break;
    }
    eligible.forEach((item, order) => { item.order = order; item.score = score(item, plan, pools.length); });
    // the other band style (Plain vs Diamond) only fills places nothing else can
    eligible.sort((a, b) => (primClash(a, plan) - primClash(b, plan)) || b.score - a.score || a.order - b.order);
    const picks = [], families = new Set();
    for (const item of eligible) {
      if (picks.length >= want) break;
      if (item.fam && families.has(item.fam)) continue;                 // one card per design
      if (item.fam) families.add(item.fam);
      picks.push(item);
    }
    if (DEBUG) {
      console.log('[related] plan', plan);
      console.table(eligible.slice(0, 16).map((i) => ({ handle: i.handle, pool: i.pool, score: +i.score.toFixed(2), price: i.price / 100, tags: i.tags.join(' ') })));
      console.log('[related] picks', picks.map((p) => p.handle).join(', '));
    }
    return picks;
  }

  function readPlan(root) {
    const el = root && root.querySelector('script[data-rp-plan]');
    if (!el) return null;
    try {
      const plan = JSON.parse(el.textContent);
      return plan && Array.isArray(plan.pools) && plan.pools.filter(Boolean).length ? plan : null;
    } catch (e) { return null; }
  }

  window.AlloyRelated = { pick, readPlan };
})();
